/**
 * @fileoverview A escolha do idioma da tela (o globo das telas de entrada): a
 * bandeira e o nome de cada idioma, com ✓ no atual. O mouse ou as setas
 * destacam, Enter, espaço ou o clique escolhem, e a tela já troca de idioma.
 * O idioma é de cada pessoa (fica neste navegador); o das mensagens
 * automáticas aos clientes é o da empresa, em Configurações → Empresa.
 */

import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Check } from "lucide-react";

import { changeLanguage, i18n } from "../../translate/i18n";
import useLanguage from "../../hooks/useLanguage";
import { LANGUAGES } from "../FlagIcons";

const indexOf = (language) => LANGUAGES.findIndex((lang) => String(language || "").startsWith(lang.code));

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const faint = "#71717A";
  const hover = dark ? "rgba(255,255,255,0.06)" : "#F4F4F5";
  const focus = dark ? "rgba(91,124,255,0.6)" : "rgba(61,92,253,0.55)";
  return {
    container: { padding: 6, minWidth: 216 },
    header: { padding: "8px 10px 6px", fontSize: 12, fontWeight: 500, color: faint },
    list: { display: "flex", flexDirection: "column", gap: 2 },
    option: {
      display: "flex", alignItems: "center", gap: 10, height: 38, padding: "0 10px", borderRadius: 8,
      fontSize: 14, fontWeight: 500, color: text, cursor: "pointer", userSelect: "none", outline: "none",
      transition: "background-color 120ms",
      "&:focus": { background: hover },
      "&:focus-visible": { boxShadow: `inset 0 0 0 1px ${focus}` },
    },
    // O contorno leve mostra o branco das bandeiras no tema claro.
    flag: {
      width: 22, height: "auto", flexShrink: 0, borderRadius: 3,
      boxShadow: `0 0 0 1px ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
    },
    name: { flex: 1 },
    check: {
      flexShrink: 0, color: text, animation: "$checkIn 160ms ease-out",
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    },
    "@keyframes checkIn": { from: { opacity: 0, transform: "scale(0.6)" }, to: { opacity: 1, transform: "none" } },
  };
});

const LanguageControl = ({ showHeader = true, onChange }) => {
  const classes = useStyles();
  const current = indexOf(useLanguage());
  const optionsRef = useRef([]);

  // Ao abrir, o foco fica no idioma atual: pelo teclado, as setas seguem dali.
  useEffect(() => {
    const option = optionsRef.current[Math.max(indexOf(i18n.language), 0)];
    if (option) option.focus();
  }, []);

  const choose = (code) => {
    if (LANGUAGES[current]?.code !== code) changeLanguage(code);
    if (onChange) onChange(code);
  };

  const handleKeyDown = (event, index) => {
    const moves = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: LANGUAGES.length - 1 };
    if (event.key in moves) {
      event.preventDefault();
      const next = optionsRef.current[(moves[event.key] + LANGUAGES.length) % LANGUAGES.length];
      if (next) next.focus();
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(LANGUAGES[index].code);
    }
  };

  return (
    <div className={classes.container}>
      {showHeader && <div className={classes.header}>{i18n.t("selectLanguage")}</div>}
      <div className={classes.list} role="menu" aria-label={i18n.t("selectLanguage")}>
        {LANGUAGES.map((lang, index) => {
          const selected = index === current;
          return (
            <div
              key={lang.code}
              ref={(element) => { optionsRef.current[index] = element; }}
              className={classes.option}
              role="menuitemradio"
              aria-checked={selected}
              tabIndex={selected || (current < 0 && index === 0) ? 0 : -1}
              onClick={() => choose(lang.code)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              // O mouse leva o destaque junto: um item destacado de cada vez.
              onMouseEnter={(event) => event.currentTarget.focus()}
            >
              <lang.Flag className={classes.flag} aria-hidden="true" />
              <span className={classes.name}>{lang.name}</span>
              {selected && <Check size={16} className={classes.check} aria-hidden="true" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageControl;
